import React from 'react';
import { Typography, colors, SvgIcon } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { usePermissions, withTranslate } from 'react-admin';
import { time } from '~/utils';
import { AvatarField } from '~/components/ra';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import { Button } from 'react-admin';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { Confirmation, EditButton, RecordTitleButton, TextField } from '~/components';
import palette from '../theme/palette';
import { Changelog } from '~/types/Changelog';
import { getName, getTypeOfIdentity } from '~/utils/identities';
import { useTranslate } from 'react-admin';
import cx from 'classnames';
import { useApi, useReferenceEntity } from '~/hooks';
import ReferenceField from './ReferenceField';
import { history } from '~/App';
import { getHintEditModeColor, useEditMode } from '~/hooks/useEditModeContext';
import { Business, Individual } from '~/types';
import { useGetChangelogsData } from '~/hooks/useGetChangelogsData';
import {
  draftIdPostfix,
  removeDraftPostfix,
  useCreateDraft,
  useDiscardDraft,
  useSubmitDraft,
} from '~/hooks/draft';
import { ArrowForwardCircleIcon } from '~/img';
import DeleteButton from '~/components/DeleteButton';
import SaveButton from '~/components/SaveButton';
import { Draft } from '~/types/Draft';
import { fetchDraft } from '~/api/draft';
import CopyButton from '~/components/CopyButton';
import { getApprovalCaseForObject } from '~/api/approvals';
import { getProfileTypeByResource } from '~/utils/profileType';
import { ApprovalCaseDetail } from '~/types/approvalCase';

interface Props {
  record: Individual | Business;
  resource: 'individuals' | 'businesses';
  avatarSource: string;
  headline: any;
  subtitle: any;
  translate: (key: string) => string;
  openDrawer: () => void;
  hasPathnameProfile: boolean;
  // refactor changelogs later
  changelogsData: Changelog[];
  setSelectedChangelog: any;
  selectedChangelog: number;
}

interface DraftProps {
  draftData: Draft | undefined;
  isLoadingDraft: boolean;
}

const useStyles = makeStyles(() => {
  return {
    gridContainer: {
      display: 'flex',
      flexGrow: 1,
    },
    root: {
      display: 'flex',
      width: '100%',
      flexGrow: 1,
    },
    avatar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: '10px',
    },
    headline: {
      marginTop: '8px',
    },
    subtitle: {
      lineHeight: '20px',
      paddingBottom: '7px',
      paddingLeft: '1px',
      color: colors.blueGrey[500],
    },
    rightContent: {
      flexGrow: 1,
      width: '300px',
      minWidth: '300px',
      flexShrink: 0,
      alignSelf: 'center',
      marginTop: '-8px',
      /* eslint-disable no-useless-computed-key */
      ['@media (min-width: 1280px)']: {
        width: '360px',
      },
    },
    changelogMode: {
      backgroundColor: palette.changelog.base,
    },
    draftMode: {
      backgroundColor: palette.draft.base,
    },
    editLockMode: {
      backgroundColor: palette.lockEdit.base,
    },
    rightContainer: {
      padding: '8px',
      flexWrap: 'nowrap',
      alignItems: 'center',
    },
    textField: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      '& > div': {
        alignItems: 'center',
        '& > div': {
          width: '145px',
          justifyContent: 'center',
          textAlign: 'center',
          /* eslint-disable no-useless-computed-key */
          ['@media (min-width: 1280px)']: {
            width: '190px',
          },
        },
      },
    },
    ellipsis: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: 'inline',
    },
    subtitleUuidRow: {
      display: 'inline-flex',
      alignItems: 'center',
    },
    subtitleUuid: {
      marginLeft: '4px',
    },
    subtitleCopyButton: {
      padding: 0,
      marginLeft: '4px',
      color: 'inherit',
    },
    subtitleCopyIcon: {
      fontSize: '0.875rem',
    },
    warning: {
      color: palette.warning.main,
    },
    skeleton: {
      display: 'inline-block',
      width: '50px',
      height: '18px',
    },
  };
});

const defaultSubtitle = (
  record: Individual | Business,
  resource: string,
  translate: any,
  classes: ReturnType<typeof useStyles>
) => {
  return (
    <>
      {record.id && (
        <span className={classes.subtitleUuidRow}>
          {`${translate(`resources.${resource}.name`, 1)} `}
          <span className={classes.subtitleUuid}>{removeDraftPostfix(record.id)}</span>
          <CopyButton
            value={record.id}
            classes={{ button: classes.subtitleCopyButton, icon: classes.subtitleCopyIcon }}
          />
        </span>
      )}
      {record.id && record.createdAt && ', '}
      {record.createdAt &&
        `${translate(`${translatePrefix}.createdOn`)} ${dateFormat(record.createdAt)}.`}{' '}
    </>
  );
};

const RecordTitle = (props: Props) => {
  const { record, resource } = props;
  const classes = useStyles();
  const { editMode } = useEditMode();

  const onlyIndividualDraft = resource === 'individuals'; // remove later
  const { permissions } = usePermissions();
  const hasResourcePermissionEdit =
    resource === 'individuals'
      ? permissions?.includes('individual.update')
      : permissions?.includes('business.update');

  const [{ data: draftData, loading: isLoadingDraft }] = useApi<Draft>(
    () =>
      onlyIndividualDraft
        ? fetchDraft(removeDraftPostfix(record?.id), resource!)
        : Promise.resolve(),
    [editMode, hasResourcePermissionEdit]
  );

  if (!record) {
    return <div />;
  }

  return (
    <div className={classes.gridContainer}>
      <ProfileData {...props} draftData={draftData} isLoadingDraft={isLoadingDraft} />
      <EditModeState {...props} draftData={draftData} isLoadingDraft={isLoadingDraft} />
    </div>
  );
};

const ProfileData = (props: Props & DraftProps) => {
  const {
    record,
    resource,
    avatarSource,
    headline,
    translate,
    openDrawer,
    hasPathnameProfile,
    setSelectedChangelog,
    draftData,
  } = props;

  const classes = useStyles();

  const { editMode, setEditMode } = useEditMode();

  const { permissions } = usePermissions();
  const hasChangelogPermissionList = permissions?.includes('individual.changelog.list');

  const lastUpdatedById = record?.lastUpdatedBy?.id;
  const lastUpdatedByType: string = getTypeOfIdentity(record?.lastUpdatedBy?.type ?? 'SYSTEM');
  const { data: lastUpdatedBy, isLoading: isLoadingLastUpdatedBy } = useReferenceEntity(
    { id: lastUpdatedById!, type: lastUpdatedByType },
    !!lastUpdatedById
  );
  const originatorName = getName?.[lastUpdatedByType]?.(lastUpdatedBy ?? {}) ?? lastUpdatedById;
  const translateLabel = useTranslate();
  const label = (key: string, arg?: any) => translateLabel(`${translatePrefix}.${key}`, arg);

  const gotoViewMode = () => {
    history.push(`/${resource}/${removeDraftPostfix(record?.id)}/show/profile`);
    if (!record.id.includes(draftIdPostfix)) {
      setEditMode('VIEW');
      setSelectedChangelog(null);
    }
  };

  const { refetch: refetchChangelogData } = useGetChangelogsData(
    resource,
    removeDraftPostfix(record?.id),
    false
  );

  // <LINK>View history</LINK>
  const viewHistoryLink = hasChangelogPermissionList ? (
    <RecordTitleButton
      label={label('history')}
      onClick={() => {
        refetchChangelogData();
        openDrawer();
      }}
    />
  ) : null;

  return (
    <div className={classes.root}>
      <div className={classes.avatar}>
        <AvatarField
          record={record}
          source={avatarSource}
          resource={resource}
          noText={true}
          size='50px'
        />
      </div>
      <div>
        {headline ? (
          <Typography variant='h2' className={classes.headline}>
            {headline(record, resource, translate)}
          </Typography>
        ) : null}
        <Typography variant='subtitle1' className={classes.subtitle}>
          {defaultSubtitle(record, resource, translate, classes)}
          {hasPathnameProfile && editMode === 'VIEW' && (
            <>
              {/* Last updated on <DATETIME> by <USERNAME> */}
              {label('lastUpdatedBy', {
                date: time(record.lastUpdatedAt).format(dateMomentFormat),
                time: time(record.lastUpdatedAt).format(timeMomentFormat),
                by: isLoadingLastUpdatedBy ? label('loading') : originatorName,
              })}
              {viewHistoryLink}
            </>
          )}
          {hasPathnameProfile && editMode === 'CHANGELOG' && (
            <>
              <span style={{ color: getHintEditModeColor(editMode) }}>
                {label('changelogWarning')}
              </span>
              {label('view')}
              <RecordTitleButton label={label('current')} onClick={gotoViewMode} />
              {'. '}
              {viewHistoryLink}
            </>
          )}
          {hasPathnameProfile && editMode === 'DRAFT' && (
            <>
              <span style={{ color: getHintEditModeColor(editMode) }}>{label('draftWarning')}</span>
              {draftData?.isUpdateDraft && (
                <>
                  {label('view')}
                  <RecordTitleButton label={label('current')} onClick={gotoViewMode} />
                  {'. '}
                  {viewHistoryLink}
                </>
              )}
            </>
          )}
          {hasPathnameProfile && editMode === 'LOCK_EDIT' && (
            <>
              <span style={{ color: getHintEditModeColor(editMode) }}>
                {label('editLockWarning')}
              </span>
              {label('view')}
              <RecordTitleButton label={label('current')} onClick={gotoViewMode} />
              {'. '}
              {viewHistoryLink}
            </>
          )}
        </Typography>
      </div>
    </div>
  );
};

const EditModeState = (props: Props & DraftProps) => {
  const {
    record,
    resource,
    changelogsData,
    setSelectedChangelog,
    selectedChangelog,
    hasPathnameProfile,
    draftData,
    isLoadingDraft,
  } = props;
  const classes = useStyles();

  const { permissions } = usePermissions();
  const hasChangelogPermissionList = permissions?.includes('individual.changelog.list');
  const hasChangelogPermissionView = permissions?.includes('individual.changelog.view');
  const hasApprovalCasePermissionView =
    permissions?.includes('approvalCase.view') || permissions?.includes('approvalCase.admin');
  const hasResourcePermissionEdit =
    resource === 'individuals'
      ? permissions?.includes('individual.update')
      : permissions?.includes('business.update');

  const lastUpdatedById: string = record?.lastUpdatedBy?.id ?? '';
  const lastUpdatedByType: string = getTypeOfIdentity(record?.lastUpdatedBy?.type ?? 'SYSTEM');
  const lastUpdatedBy = { id: lastUpdatedById, type: lastUpdatedByType };

  const { editMode } = useEditMode();

  const onlyIndividualDraft = resource === 'individuals'; // todo remove later
  const hasActiveDraft = !!draftData;

  const [{ data: approvalCases }] = useApi<Partial<ApprovalCaseDetail>[]>(
    () =>
      onlyIndividualDraft && editMode === 'VIEW'
        ? getApprovalCaseForObject(getProfileTypeByResource(resource)!, record.id)
        : Promise.resolve(),
    [editMode]
  );
  const existingUpdateCase = approvalCases?.find((c) => c?.actionType === 'UPDATE');

  const { mutateAsync: createDraft } = useCreateDraft();
  const onCreateDraft = () => {
    createDraft({ resource, record }).then((res) => {
      history.push(`/${resource}/${res?.id}${draftIdPostfix}/show/profile`);
    });
  };

  const { mutateAsync: submitMutate, isLoading: isSubmitLoading } = useSubmitDraft();
  const onSubmitDraft = () => {
    submitMutate({ resource, draftId: record?.id }).then(() => {
      history.push(`/${resource}/${removeDraftPostfix(record?.id)}/show/profile`);
    });
  };

  const { mutateAsync: discardMutate, isLoading: isDiscardLoading } = useDiscardDraft();
  const onDiscardDraft = () => {
    discardMutate({ resource, draftId: record?.id }).then(() => {
      if (draftData?.isUpdateDraft) {
        history.push(`/${resource}/${removeDraftPostfix(record?.id)}/show/profile`);
      } else {
        history.push(`/${resource}/drafts`);
      }
    });
  };

  const onEditCurrentDraft = () => {
    history.push(`/${resource}/${record?.id}${draftIdPostfix}/show/profile`);
  };

  // Current index of selected changelog.
  const currentItemIndex = changelogsData.findIndex((item) => item.id === selectedChangelog);
  // Navigation to prev and next changelog.
  const goToPrevChangelog = () => {
    setSelectedChangelog((prevId: number) => {
      const currentIndex = changelogsData.findIndex((item) => item.id === prevId);
      const nextIndex = currentIndex < changelogsData.length - 1 ? currentIndex + 1 : currentIndex;
      return changelogsData[nextIndex].id;
    });
  };
  const goToNextChangelog = () => {
    setSelectedChangelog((prevId: number) => {
      const currentIndex = changelogsData.findIndex((item) => item.id === prevId);
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : currentIndex;
      return changelogsData[prevIndex].id;
    });
  };

  const translateLabel = useTranslate();
  const label = (key: string, arg?: any) => translateLabel(`${translatePrefix}.${key}`, arg);

  if (!hasPathnameProfile) {
    return null;
  }

  // creating new or editing existing draft is locked (other draft is awaiting approval)
  if (editMode === 'VIEW' && !isLoadingDraft && existingUpdateCase && onlyIndividualDraft) {
    return (
      <div className={classes.rightContent}>
        <Paper className={classes.editLockMode}>
          <Grid className={classes.rightContainer} container justify='space-between'>
            <Grid container justify='space-between'>
              <Grid item>
                <TextField>{label('lockEditTitle')}</TextField>
                <TextField label={label('lockEditDescription')}> </TextField>
              </Grid>
              {hasApprovalCasePermissionView && (
                <Grid item>
                  <Button
                    label={label('viewCase')}
                    href={`/approvals/queue/${existingUpdateCase.id}/show`}
                    target='_blank'
                    onClick={(e: any) => e.stopPropagation()}
                  >
                    <SvgIcon>
                      <ArrowForwardCircleIcon />
                    </SvgIcon>
                  </Button>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Paper>
      </div>
    );
  }

  // offer to create new draft
  if (
    editMode === 'VIEW' &&
    !isLoadingDraft &&
    !hasActiveDraft &&
    onlyIndividualDraft &&
    hasResourcePermissionEdit
  ) {
    return (
      <div className={classes.rightContent}>
        <Paper>
          <Grid className={classes.rightContainer} container justify='space-between'>
            <Grid container justify='space-between'>
              <Grid item>
                <TextField>{label('proposeChange')}</TextField>
                <TextField label={label('draftWillBeCreated')}> </TextField>
              </Grid>
              <Grid item>
                <EditButton label={translateLabel(`ra.action.edit`)} onClick={onCreateDraft} />
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </div>
    );
  }

  // offer to edit existing draft
  if (editMode === 'VIEW' && !isLoadingDraft && hasActiveDraft && onlyIndividualDraft) {
    return (
      <div className={classes.rightContent}>
        <Paper className={classes.draftMode}>
          <Grid className={classes.rightContainer} container justify='space-between'>
            <Grid container justify='space-between'>
              <Grid item>
                <TextField>{label('haveDraft')}</TextField>
                {draftData && (
                  <TextField label={`${label('created')} ${dateFormat(draftData.createdAt)}`}>
                    {' '}
                  </TextField>
                )}
              </Grid>
              <Grid item>
                <Button label={label('viewDraft')} onClick={onEditCurrentDraft}>
                  <SvgIcon>
                    <ArrowForwardCircleIcon />
                  </SvgIcon>
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </div>
    );
  }

  // editing current draft
  if (editMode === 'DRAFT' && onlyIndividualDraft) {
    return (
      <div className={classes.rightContent}>
        <Paper className={classes.draftMode}>
          <Grid className={classes.rightContainer} container justify='space-between'>
            <Grid container justify='space-between' alignItems='center'>
              <Grid item>
                <TextField>{label('myDraft')}</TextField>
                {draftData && (
                  <TextField label={`${label('created')} ${dateFormat(draftData.createdAt)}`}>
                    {' '}
                  </TextField>
                )}
              </Grid>
              <Grid item>
                <Grid container spacing={1} alignItems='center'>
                  <Grid item>
                    <Confirmation
                      onConfirm={onDiscardDraft}
                      confirmationSettings={{
                        variant: 'modal',
                        modal: {
                          heading: label('discardConfirmationTitle'),
                          content: label('discardConfirmationBody'),
                        },
                      }}
                    >
                      <DeleteButton
                        label={label('discard')}
                        loading={isSubmitLoading || isDiscardLoading}
                      />
                    </Confirmation>
                  </Grid>
                  <Grid item>
                    <Confirmation
                      onConfirm={onSubmitDraft}
                      confirmationSettings={{
                        variant: 'modal',
                        modal: {
                          heading: label('submitConfirmationTitle'),
                          content: label('submitConfirmationBody'),
                        },
                      }}
                    >
                      <SaveButton
                        label={label('submit')}
                        size='small'
                        loading={isSubmitLoading || isDiscardLoading}
                      />
                    </Confirmation>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </div>
    );
  }

  // viewing current draft awaiting approval
  if (editMode === 'LOCK_EDIT' && onlyIndividualDraft) {
    return (
      <div className={classes.rightContent}>
        <Paper className={classes.editLockMode}>
          <Grid className={classes.rightContainer} container justify='space-between'>
            <Grid container justify='space-between' alignItems='center'>
              <Grid item>
                <TextField>{label('submittedDraft')}</TextField>
                {/* todo add submitted date */}
                <TextField label={`${label('submitted')}`}> </TextField>
              </Grid>
              <Grid item>
                <Grid container spacing={2} alignItems='center'>
                  <Grid item>
                    <Confirmation
                      onConfirm={onDiscardDraft}
                      confirmationSettings={{
                        variant: 'modal',
                        modal: {
                          heading: label('discardConfirmationTitle'),
                          content: label('discardConfirmationBody'),
                        },
                      }}
                    >
                      <DeleteButton label={label('discard')} />
                    </Confirmation>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </div>
    );
  }

  // viewing changelog
  if (editMode === 'CHANGELOG' && hasChangelogPermissionList && hasChangelogPermissionView) {
    return (
      <div className={classes.rightContent}>
        <Paper className={classes.changelogMode}>
          <Grid className={classes.rightContainer} container justify='space-between'>
            <Grid item>
              <Button
                label={label('prev')}
                onClick={() => goToPrevChangelog()}
                disabled={currentItemIndex === changelogsData.length - 1}
              >
                <ChevronLeftIcon />
              </Button>
            </Grid>
            <Grid item>
              <ReferenceField
                className={cx(classes.textField, classes.ellipsis)}
                label={`${label('version')} ${dateFormat(record?.lastUpdatedAt)}`}
                record={lastUpdatedBy}
              />
            </Grid>
            <Grid item>
              <Button
                label={label('next')}
                alignIcon='right'
                onClick={() => goToNextChangelog()}
                disabled={currentItemIndex === 0}
              >
                <ChevronRightIcon />
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </div>
    );
  }
  return null;
};

const dateMomentFormat = 'l';
const timeMomentFormat = 'LT';
const dateFormat = (dateString: string) => {
  return `${time(dateString).format(dateMomentFormat)} ${time(dateString).format(
    timeMomentFormat
  )}`;
};

const translatePrefix = `components.RecordTitle`;

export default withTranslate(RecordTitle);
